// eslint-disable-next-line import/named
import { html, TemplateResult } from 'lit';
import { TabAction } from './tab-manager-types';
import { when } from 'lit/directives/when.js';

export type TabActionDropdownPlacement = 'top' | 'top-start' | 'bottom' | 'bottom-start';

/** Render context supplied by the TabManager so buttons share its lock state and executor. */
export interface TabActionRenderContext {
  /** True while the manager is mid-transition; all buttons render disabled. */
  locked: boolean;
  /** Which side dropdown menus open on (footer opens up, nav strip opens down). */
  dropdownPlacement: TabActionDropdownPlacement;
  /** Executes the action's onClick inside the manager's UI lock. */
  run: (action: TabAction) => Promise<void> | void;
}

/** True when the click landed on a real link, such as a webmodule-button with an href inside a caption. */
function isLinkClick(e: Event): boolean {
  return e.composedPath().some(el => el instanceof HTMLAnchorElement && el.hasAttribute('href'));
}

function actionClickEvent(action: TabAction, context: TabActionRenderContext) {
  return async (e: Event) => {
    e.stopPropagation();
    const blocked = action.disabled || context.locked;
    // Let a link inside the caption navigate (e.g. download links on issued quote reports).
    if (blocked || !isLinkClick(e)) e.preventDefault();
    if (blocked) return;
    await context.run(action);
  };
}

function buttonTemplate(action: TabAction, context: TabActionRenderContext): TemplateResult {
  return html` <webmodule-button
    size="small"
    variant="primary"
    class="${action.classList ?? ''}"
    @click=${actionClickEvent(action, context)}
    ?disabled=${action.disabled || context.locked}
  >
    ${action.caption?.()}
  </webmodule-button>`;
}

function menuItemTemplate(action: TabAction, context: TabActionRenderContext): TemplateResult {
  if (action.heading)
    return html`<small class="tab-manager__menu-heading">${action.caption?.()}</small>
      <webmodule-divider></webmodule-divider>`;
  const runAction = actionClickEvent(action, context);
  return html`
    <webmodule-menu-item
      @click=${async (e: Event) => {
        closeOwningDropdown(e);
        await runAction(e);
      }}
      ?disabled=${action.disabled || context.locked}
    >
      ${action.caption?.()}
    </webmodule-menu-item>
  `;
}

// Why close it by hand? Because actionClickEvent stops the click before it reaches webmodule-menu,
// so the menu never emits the webmodule-select that makes the dropdown close itself. Closing first
// also keeps the menu from sitting over any confirmation the action opens.
function closeOwningDropdown(e: Event): void {
  void (e.currentTarget as HTMLElement | null)
    ?.closest<HTMLElement & { hide?: () => Promise<void> }>('webmodule-dropdown')
    ?.hide?.();
}

/**
 * Renders a TabAction as a button, a dropdown (when it has items), or a split
 * button group (when it has both onClick and items).
 */
export function tabActionTemplate(action: TabAction, context: TabActionRenderContext): TemplateResult {
  if (!action.items?.length) {
    return buttonTemplate(action, context);
  }

  const caretUp = context.dropdownPlacement.startsWith('top');
  const trigger = html`
    <webmodule-button
      size="small"
      variant="primary"
      class="${action.classList ?? ''}"
      slot="trigger"
      caret
      ?caretup=${caretUp}
      ?disabled=${context.locked}
    >
      ${when(!action.onClick, () => html`${action.caption?.()}`)}
    </webmodule-button>
  `;
  const dropdown = html` <webmodule-dropdown placement=${context.dropdownPlacement} distance="5">
    ${trigger}
    <webmodule-menu> ${action.items.map(item => menuItemTemplate(item, context))}</webmodule-menu>
  </webmodule-dropdown>`;

  return action.onClick
    ? html` <webmodule-button-group label="Page actions">
        ${buttonTemplate(action, context)} ${dropdown}
      </webmodule-button-group>`
    : dropdown;
}
